import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Archive } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { PageHeader, DataTable, PrimaryButton, GhostButton } from "@/components/admin/ui";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";

const toForm = (fields, item) => Object.fromEntries(fields.map((f) => {
  const v = item?.[f.key];
  if (f.type === "list") return [f.key, (v || []).join("\n")];
  if (f.type === "codes") return [f.key, (v || []).join(", ")];
  if (f.type === "bool") return [f.key, v ?? true];
  if (f.type === "multi") return [f.key, v || []];
  return [f.key, v ?? ""];
}));

const toPayload = (fields, form) => Object.fromEntries(fields.map((f) => {
  const v = form[f.key];
  if (f.type === "list") return [f.key, v.split("\n").map((s) => s.trim()).filter(Boolean)];
  if (f.type === "codes") return [f.key, v.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean)];
  if (f.type === "number") return [f.key, v === "" ? undefined : Number(v)];
  return [f.key, v];
}));

function FieldInput({ field, value, onChange, options }) {
  const tid = `crud-field-${field.key}`;
  if (field.type === "textarea" || field.type === "list") return <Textarea rows={field.type === "list" ? 4 : 3} value={value} onChange={(e) => onChange(e.target.value)} required={field.required} data-testid={tid} />;
  if (field.type === "bool") return <Switch checked={value} onCheckedChange={onChange} data-testid={tid} />;
  if (field.type === "multi") return (
    <div className="grid sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto rounded-xl border border-[#E9DFE7] p-3" data-testid={tid}>
      {options?.map((o) => (
        <label key={o.id} className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox checked={value.includes(o.id)} onCheckedChange={(c) => onChange(c ? [...value, o.id] : value.filter((x) => x !== o.id))} />{o.name}
        </label>
      ))}
    </div>
  );
  return <Input type={field.type === "number" ? "number" : "text"} value={value} onChange={(e) => onChange(e.target.value)} required={field.required} placeholder={field.placeholder} data-testid={tid} />;
}

function CrudDialog({ state, onClose, fields, endpoint, options, onDone, title }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm(state ? toForm(fields, state.item) : null); }, [state, fields]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = toPayload(fields, form);
      if (state.item) await api.put(`${endpoint}/${state.item.id}`, payload);
      else await api.post(endpoint, payload);
      toast.success("Saved. Dataset version updated.");
      onClose();
      onDone();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!state} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white rounded-2xl sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="crud-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">{state?.item ? `Edit ${title}` : `Add ${title}`}</DialogTitle><DialogDescription>Changes create a new dataset version. Reports already generated never change.</DialogDescription></DialogHeader>
        {form && (
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
            {fields.map((f) => (
              <div key={f.key} className={`space-y-2 ${f.wide || ["textarea", "list", "multi"].includes(f.type) ? "sm:col-span-2" : ""}`}>
                <Label className="font-semibold">{f.label}</Label>
                <FieldInput field={f} value={form[f.key]} onChange={(v) => setForm({ ...form, [f.key]: v })} options={options} />
                {f.hint && <p className="text-xs text-[#635060]">{f.hint}</p>}
              </div>
            ))}
            <div className="sm:col-span-2"><PrimaryButton type="submit" busy={busy} className="w-full" data-testid="crud-save-button">Save</PrimaryButton></div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function CrudManager({ endpoint, eyebrow, title, itemLabel, description, fields, columns, options, testId }) {
  const { data, loading, reload } = useFetch(endpoint);
  const [state, setState] = useState(null);
  const deactivate = async (item) => {
    try { await api.delete(`${endpoint}/${item.id}`); toast.success("Deactivated"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const actions = { key: "actions", label: "", render: (r) => (
    <div className="flex gap-2 justify-end">
      <GhostButton onClick={() => setState({ item: r })} data-testid={`${testId}-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></GhostButton>
      {r.active && <GhostButton onClick={() => deactivate(r)} data-testid={`${testId}-deactivate-${r.id}`}><Archive className="h-3.5 w-3.5" /></GhostButton>}
    </div>
  ) };
  return (
    <div data-testid={`${testId}-page`}>
      <PageHeader eyebrow={eyebrow} title={title} description={description} actions={<PrimaryButton onClick={() => setState({ item: null })} data-testid={`${testId}-add-button`}><Plus className="h-4 w-4" />Add {itemLabel}</PrimaryButton>} />
      <DataTable testId={`${testId}-table`} loading={loading} rows={data} columns={[...columns, { key: "active", label: "Status", render: (r) => (r.active ? "Active" : "Inactive") }, actions]} />
      <CrudDialog state={state} onClose={() => setState(null)} fields={fields} endpoint={endpoint} options={options} onDone={reload} title={itemLabel} />
    </div>
  );
}
